/**
 * @fileoverview Body of the student tracker for one project: summary, lesson
 * list (table/cards), final block, legend, empty/loading states and the
 * drawers. Shared by the standalone page and the project "Tasks tracker" tab
 * so both behave alike.
 */

import { useCallback, useMemo } from 'react'
import { Stack } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'
import { useMediaQuery } from 'hooks'
import { FINISHED_PROJECT_STATUS, MOBILE_BREAKPOINT, todayIsoDate } from '../constants'
import { useStudentTracker } from '../hooks/useStudentTracker'
import { useStudentTrackerDrawers } from '../hooks/useStudentTrackerDrawers'
import { StudentTrackerEmptyState } from './StudentTrackerEmptyState'
import { StudentTrackerLoadingState } from './StudentTrackerLoadingState'
import { StudentTrackerOverlays } from './StudentTrackerOverlays'
import { StudentTrackerProcessingNotice } from './StudentTrackerProcessingNotice'
import { StudentTrackerMatrix } from './StudentTrackerMatrix'
import { StudentTrackerSummary } from './StudentTrackerSummary'

interface StudentTrackerContentProps {
  projectId: string
  /** Project name/status when the caller knows them (standalone page); the tab omits them. */
  projectName?: string
  projectStatus?: string
  /** Show the project name + "View project" link in the summary (standalone page). */
  showProjectLink?: boolean
}

const TOAST_ICON_SIZE = 18

export function StudentTrackerContent({
  projectId,
  projectName,
  projectStatus,
  showProjectLink = false,
}: StudentTrackerContentProps) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)

  const showLoadError = useCallback(() => {
    notifications.show({
      message: t('student_task_tracker.error.load_failed'),
      color: 'red',
      icon: <IconX size={TOAST_ICON_SIZE} />,
    })
    // `t` from i18next is not referentially stable — intentionally not a dependency.
  }, [])

  const tracker = useStudentTracker({ projectId, onLoadError: showLoadError })
  const drawers = useStudentTrackerDrawers()
  const { matrix, loading, state } = tracker

  const projectFinished = projectStatus === FINISHED_PROJECT_STATUS
  const editable = Boolean(matrix?.editable) && !projectFinished
  const today = useMemo(() => todayIsoDate(), [])

  const hasTasks = Boolean(matrix && (matrix.lessons.length > 0 || matrix.final.present))

  let body: JSX.Element | null = null
  if (loading) {
    body = <StudentTrackerLoadingState />
  } else if (state === 'not_enrolled') {
    body = <StudentTrackerEmptyState variant='not_enrolled' />
  } else if (state === 'error') {
    body = <StudentTrackerEmptyState variant='error' onRetry={tracker.reload} />
  } else if (matrix && !hasTasks) {
    body = <StudentTrackerEmptyState variant='no_tasks' />
  } else if (matrix) {
    body = (
      <StudentTrackerMatrix
        matrix={matrix}
        editable={editable}
        today={today}
        onOpenDeliverable={drawers.openDeliverable}
        onOpenSelfAssessment={drawers.openSelfAssessment}
        onOpenTeacherAssessment={drawers.openTeacherAssessment}
        onOpenFinal={drawers.openFinal}
      />
    )
  }

  return (
    <Stack spacing='lg'>
      {matrix && hasTasks && !loading && (
        <StudentTrackerSummary
          summary={matrix.summary}
          projectId={showProjectLink ? projectId : undefined}
          projectName={showProjectLink ? projectName : undefined}
          projectCompleted={projectFinished}
          isMobile={isMobile}
        />
      )}

      {matrix?.processing && !loading && <StudentTrackerProcessingNotice />}

      {body}

      <StudentTrackerOverlays
        drawers={drawers}
        projectId={projectId}
        projectName={projectName}
        studentId={matrix?.studentId ?? ''}
        onChanged={tracker.reloadSilent}
      />
    </Stack>
  )
}
